import { useCreateMutation } from '@gripp/shared-logic';
import {
  Colors,
  ControlledTextInput,
  SaveFormTop,
  Spacing,
  TopModalBar,
  useAutoFocusRef,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import * as yup from 'yup';

const CREATE_ADDON = `
  mutation createAddon($input: CreateAddonInput!) {
    createAddon(input: $input) {
      id
      name
      workspace {
        id
        name
      }
    }
  }
`;

type Props = {
  onComplete: () => void;
  workspaceId: string;
};

export const CreateAddonForm = ({ workspaceId, onComplete }: Props) => {
  const { t } = useTranslation();
  const { mutateAsync, isPending } = useCreateMutation({
    modelName: 'addon',
    query: CREATE_ADDON,
  });

  const formSchema = yup.object({
    name: yup.string().required(t('addOn.create.validation.name_required')),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: { name: '' },
  });

  const onSubmit = async (data: yup.InferType<typeof formSchema>) => {
    const input = {
      name: data.name,
      workspace: { id: workspaceId },
    };
    await mutateAsync(
      { input },
      {
        onSettled: (data: any, error?: Error) => {
          if (error) return Promise.reject(error);
          onComplete();
        },
      }
    );
  };

  const elementFocusCallBack = useAutoFocusRef();

  return (
    <View style={styles.contentContainer}>
      <SaveFormTop
        isDirty={isDirty && !isPending}
        isValid={isValid && !isPending}
        onSubmit={handleSubmit(onSubmit)}
        title={t('addOn.create.label')}
        saveLabel={t('addOn.create.create')}
        topBar={TopModalBar}
        onCancel={onComplete}
      >
        <View style={styles.formItem}>
          <View>
            <Text style={styles.formItemTitle}>{t('addOn.list.name')}</Text>
          </View>
          <ControlledTextInput
            control={control}
            name="name"
            placeholder={t('addOn.create.placeholder')}
            innerRef={elementFocusCallBack}
          />
        </View>
      </SaveFormTop>
    </View>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    width: '100%',
  },
  header: {
    paddingVertical: 15,
    paddingHorizontal: 20,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  formItemTitle: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 16,
    marginBottom: 10,
    color: Colors.role.textStrong,
  },
  formItem: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingTop: Spacing.basePadding.paddingVertical,
  },
});
